import Logger from "./Logger.ts";
import {
  APP_VERSION_KEY,
  KLARITICS_CONFIG_KEY,
  KLARITICS_INSTALL_KEY,
  KLARITICS_LAST_BG_TIME_KEY,
  KLARITICS_LAST_EVENT_TIME,
  KLARITICS_SESSION_ID,
  KLARITICS_SYNC,
  KLARITICS_USER_ATTR_KEY,
  KLARITICS_USER_ID,
  KLARITICS_CURRENT_CHUNK,
  OLD_SESSIONS_DATA,
  KLARITICS_LT_COUNT,
  KLARITICS_UNSENT_CHUNK_DETAILS,
  KLARITICS_SESSION_CHUNK_NUMBER_MAP,
  KLARITICS_LAST_CONFIG_TIME,
  KLARITICS_CONFIG_HASH,
  KLARITICS_LAST_ENABLED_FLAG,
  KLARITICS_LAST_ENABLE_DISABLE_TIME
} from "./constants.ts";

interface StorageFallback {
  length: number;
  getItem: (key?: string) => undefined;
  setItem: (key?: string, value?: unknown) => void;
  removeItem: (key?: string) => void;
  clear: () => void;
  get: () => undefined;
  set: () => void;
  remove: () => void;
  getParsed: () => undefined;
}

export class Storage {
  _isStorageAvailable = false;
  storageType: string;
  storage: globalThis.Storage | StorageFallback;

  constructor(storageType: string) {
    this._isStorageAvailable = this._isSupported(storageType);
    this.storageType = storageType;

    if (!this._isStorageAvailable) {
      const f = () => undefined;
      this.storage = {
        length: 0,
        getItem: f,
        setItem: f,
        removeItem: f,
        clear: f,
        get: f,
        set: f,
        remove: f,
        getParsed: f
      };
    } else {
      this.storage = window[storageType as "localStorage" | "sessionStorage"];
    }
  }

  static waitForBrowserStorage(callBack: () => void): void {
    let maxTestsForStorage = 15;
    const storageWaitIntervalId = setInterval(() => {
      maxTestsForStorage--;
      if (Storage.isLocalStorageEnabled() || maxTestsForStorage <= 0) {
        Logger.debug("Storage : Local storage available");
        clearInterval(storageWaitIntervalId);
        callBack();
      } else {
        Logger.debug("Storage : Local storage not available yet");
      }
    }, 100);
  }

  static isLocalStorageEnabled(): boolean {
    try {
      const key = `__KLARITICS_storage_test__`;
      window.localStorage.setItem(key, "test");
      const value = window.localStorage.getItem(key);
      window.localStorage.removeItem(key);
      return value === "test";
    } catch (e) {
      Logger.debug(`Browser storage is not enabled yet : ${e}`);
      return false;
    }
  }

  private _isSupported = (storageType: string): boolean => {
    let supported = true;
    try {
      const key = "_KLARITICS_";
      const val = "KLARITICS";
      if (
        (window as unknown as Record<string, globalThis.Storage>)[storageType]
      ) {
        (window as unknown as Record<string, globalThis.Storage>)[
          storageType
        ].setItem(key, val);
        if (
          (window as unknown as Record<string, globalThis.Storage>)[
            storageType
          ].getItem(key) !== val
        ) {
          supported = false;
        }
        (window as unknown as Record<string, globalThis.Storage>)[
          storageType
        ].removeItem(key);
      } else {
        supported = false;
      }
    } catch (err) {
      supported = false;
    }
    if (!supported) Logger.error("Storage : LocalStorage not supported");
    return supported;
  };

  get = (key: string): string | null => {
    try {
      return this.storage.getItem(key) ?? null;
    } catch (err) {
      Logger.error(
        `Storage : Error while getting the ${key} from storage - ${err}`
      );
    }
    return null;
  };

  getParsed = <T = unknown>(key: string): T | null => {
    try {
      return JSON.parse(this.storage.getItem(key) as string) as T;
    } catch (err) {
      Logger.error(
        `Storage : Error while parsing ${key}'s value from storage - ${err}`
      );
    }
    return null;
  };

  set = (key: string, value: string): void => {
    try {
      this.storage.setItem(key, value);
    } catch (err) {
      Logger.error(
        `Storage : Error while setting the ${key}'s value to storage - ${err}`
      );
    }
  };

  remove = (key: string): void => {
    try {
      this.storage.removeItem(key);
    } catch (err) {
      Logger.error(
        `Storage : Error while removing the ${key} from storage - ${err}`
      );
    }
  };

  clear = (): void => {
    try {
      this.storage.removeItem(KLARITICS_CONFIG_KEY);
      this.storage.removeItem(KLARITICS_USER_ATTR_KEY);
      this.storage.removeItem(KLARITICS_LAST_EVENT_TIME);
      this.storage.removeItem(KLARITICS_SESSION_ID);
      this.storage.removeItem(KLARITICS_SYNC);
      this.storage.removeItem(KLARITICS_USER_ID);
      this.storage.removeItem(KLARITICS_INSTALL_KEY);
      this.storage.removeItem(APP_VERSION_KEY);
      this.storage.removeItem(KLARITICS_LAST_BG_TIME_KEY);
      this.storage.removeItem(KLARITICS_CURRENT_CHUNK);
      this.storage.removeItem(OLD_SESSIONS_DATA);
      this.storage.removeItem(KLARITICS_LT_COUNT);
      this.storage.removeItem(KLARITICS_UNSENT_CHUNK_DETAILS);
      this.storage.removeItem(KLARITICS_SESSION_CHUNK_NUMBER_MAP);
      this.storage.removeItem(KLARITICS_LAST_CONFIG_TIME);
      this.storage.removeItem(KLARITICS_CONFIG_HASH);
      this.storage.removeItem(KLARITICS_LAST_ENABLED_FLAG);
      this.storage.removeItem(KLARITICS_LAST_ENABLE_DISABLE_TIME);
    } catch (err) {
      Logger.error(`Storage : Error while clearing the storage - ${err}`);
    }
  };

  isStorageAvailable(): boolean {
    return this._isStorageAvailable;
  }
}

export default function createStorage(): Storage {
  return new Storage("localStorage");
}

export function sessionStorage(): Storage {
  return new Storage("sessionStorage");
}
